
import { BASE_URL } from '../config';
import { useAuthStore } from '../stores/useAuthStore';

export interface AddVenereUserRequest {
  email: string;
  featureType: string;
  environment: string;
  notes?: string;
}

export interface AddVenereUserResponse {
  success: boolean;
  message: string;
  user?: string;
}

export interface ExperimentalUser {
  id: string;
  email: string;
  featureType: string;
  environment: string;
  addedBy: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExperimentalUserBackend {
  id: string;
  email: string;
  feature_type: string;
  added_by: string;
  notes?: string;
  experiment_metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export class AdminService {
  /**
   * Get experimental user by email
   */
  static async getExperimentalUserByEmail(email: string): Promise<ExperimentalUserBackend | null> {
    try {
      // Get auth token if available
      const authStore = useAuthStore.getState();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authStore.auth.idToken) {
        headers['Authorization'] = `Bearer ${authStore.auth.idToken}`;
      }

      console.log(`Getting experimental user by email: ${email}`);

      const response = await fetch(`${BASE_URL}/api/experimental-users/email/${encodeURIComponent(email)}`, {
        method: 'GET',
        headers,
      });

      if (response.status === 404) {
        // User not found in experimental users
        console.log(`User ${email} not found in experimental users`);
        return null;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error getting experimental user:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const userData = await response.json();
      console.log('Experimental user data received:', userData);
      
      return userData;
    } catch (error) {
      console.error('Error getting experimental user by email:', error);
      return null;
    }
  }

  /**
   * Check if user has experimental access
   */
  static async checkExperimentalAccess(email: string, featureType: string, environment: string): Promise<boolean> {
    try {
      const user = await this.getExperimentalUserByEmail(email);
      
      if (!user) {
        return false;
      }

      // Check if user has the specific feature type
      return user.feature_type === featureType;
    } catch (error) {
      console.error('Error checking experimental access:', error);
      return false;
    }
  }

  /**
   * Add a user to the experimental program
   */
  static async addExperimentalUser(request: AddVenereUserRequest): Promise<AddVenereUserResponse> {
    try {
      // Get current user for added_by field
      const currentUser = 'admin@holifya.com'; // TODO: Get from auth store
      
      // Transform request to match backend schema
      const backendRequest = {
        email: request.email,
        feature_type: request.featureType, // Convert to snake_case
        added_by: currentUser,
        notes: request.notes,
        experiment_metadata: {
          environment: request.environment,
          added_via: 'admin_dashboard'
        }
      };

      // Get auth token if available
      const authStore = useAuthStore.getState();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Add authorization header if token is available
      if (authStore.auth.idToken) {
        headers['Authorization'] = `Bearer ${authStore.auth.idToken}`;
      }

      console.log('Sending request to backend:', {
        url: `${BASE_URL}/api/experimental-users`,
        method: 'POST',
        headers,
        body: backendRequest
      });

      const response = await fetch(`${BASE_URL}/api/experimental-users`, {
        method: 'POST',
        headers,
        body: JSON.stringify(backendRequest),
      });

      console.log('Backend response status:', response.status);
      console.log('Backend response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error response:', errorText);
        
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // If error response is not JSON, use the text as is
        }
        
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Backend success response:', result);
      
      return {
        success: true,
        message: `Successfully added ${request.email} to ${request.featureType} users in ${request.environment} environment`,
        user: request.email
      };
    } catch (error) {
      console.error('Error adding experimental user:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to add experimental user'
      };
    }
  }

  /**
   * Remove a user from the experimental program
   */
  static async removeExperimentalUser(userId: string): Promise<AddVenereUserResponse> {
    try {
      // Get auth token if available
      const authStore = useAuthStore.getState();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authStore.auth.idToken) {
        headers['Authorization'] = `Bearer ${authStore.auth.idToken}`;
      }

      const response = await fetch(`${BASE_URL}/api/experimental-users/${userId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // If error response is not JSON, use the text as is
        }
        throw new Error(errorMessage);
      }

      return {
        success: true,
        message: 'Successfully removed user from experimental program',
      };
    } catch (error) {
      console.error('Error removing experimental user:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to remove experimental user'
      };
    }
  }

  /**
   * Get experimental users by feature type using the specific endpoint
   */
  static async getExperimentalUsersByFeatureType(featureType: string): Promise<ExperimentalUser[]> {
    try {
      // Get auth token if available
      const authStore = useAuthStore.getState();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authStore.auth.idToken) {
        headers['Authorization'] = `Bearer ${authStore.auth.idToken}`;
      }

      const response = await fetch(`${BASE_URL}/api/experimental-users/feature-type/${encodeURIComponent(featureType)}`, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // If error response is not JSON, use the text as is
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Transform backend response to frontend format
      return data.map((user: any) => ({
        id: user.id,
        email: user.email,
        featureType: user.feature_type,
        environment: user.experiment_metadata?.environment || 'dev',
        addedBy: user.added_by,
        notes: user.notes,
        createdAt: user.created_at,
        updatedAt: user.updated_at,
      }));
    } catch (error) {
      console.error('Error fetching experimental users by feature type:', error);
      throw error;
    }
  }

  /**
   * Get the current list of experimental users
   */
  static async getExperimentalUsers(featureType: string, environment: string): Promise<ExperimentalUser[]> {
    try {
      // Get auth token if available
      const authStore = useAuthStore.getState();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (authStore.auth.idToken) {
        headers['Authorization'] = `Bearer ${authStore.auth.idToken}`;
      }

      const response = await fetch(`${BASE_URL}/api/experimental-users?feature_type=${featureType}&environment=${environment}`, {
        method: 'GET',
        headers,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch (e) {
          // If error response is not JSON, use the text as is
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Transform backend response to frontend format
      return data.map((user: any) => ({
        id: user.id,
        email: user.email,
        featureType: user.feature_type,
        environment: user.experiment_metadata?.environment || 'dev',
        addedBy: user.added_by,
        notes: user.notes,
        createdAt: user.created_at,
        updatedAt: user.updated_at,
      }));
    } catch (error) {
      console.error('Error fetching experimental users:', error);
      throw error;
    }
  }

  /**
   * Determine the current environment based on the URL
   */
  private static getCurrentEnvironment(): string {
    const hostname = window.location.hostname;
    
    if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
      return 'dev'; // Local development
    } else if (hostname.includes('staging') || hostname.includes('stage')) {
      return 'stage';
    } else if (hostname.includes('production') || hostname.includes('prod')) {
      return 'prod';
    } else {
      // Default to dev for unknown environments
      return 'dev';
    }
  }

  /**
   * Get environment info for display
   */
  static getEnvironmentInfo(): { name: string; displayName: string } {
    const env = this.getCurrentEnvironment();
    
    const envMap = {
      dev: { name: 'dev', displayName: 'Development' },
      stage: { name: 'stage', displayName: 'Staging' },
      prod: { name: 'prod', displayName: 'Production' }
    };
    
    return envMap[env as keyof typeof envMap] || envMap.dev;
  }
} 